/**
 * Phase FIRST-INV (Part B) — Draft modal for manual first invoice.
 *
 * Handles BOTH create (POST /admin/rentals/{id}/first-invoice/draft) and
 * edit-in-draft-status (PATCH /admin/invoices/{id}). On [Issue invoice]
 * the modal fires an inline confirm then POSTs /admin/invoices/{id}/issue.
 *
 * Auto-populated line items on OPEN (create path only):
 *   Row 1 — "First month rent (<start> – <end>)" · line_type=rent
 *   Row 2 (when rental.deposit.amount_cents > 0) — "Security deposit" · line_type=security_deposit
 *
 * No tax UI. Subtotal + TOTAL only.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
const LINE_TYPES = [
    { value: "rent", label: "Rent" },
    { value: "security_deposit", label: "Security deposit" },
    { value: "fee", label: "Fee" },
    { value: "discount", label: "Discount" },
    { value: "damage", label: "Damage" },
    { value: "adjustment", label: "Adjustment" },
    { value: "other", label: "Other" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
    const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};
const monthEndIso = (startIso) => {
    if (!startIso) return startIso;
    const d = new Date(startIso + "T00:00:00Z");
    d.setUTCMonth(d.getUTCMonth() + 1);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
};
const dollarsFromCents = (c) => (Number(c || 0) / 100).toFixed(2);
const centsFromDollars = (d) => {
    const n = parseFloat(String(d).replace(/,/g, ""));
    if (!isFinite(n)) return 0;
    return Math.round(n * 100);
};
const money = (cents) => `$${(Number(cents || 0) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

function newRow(overrides = {}) {
    return {
        _key: `row-${Math.random().toString(36).slice(2, 10)}`,
        description: "",
        quantity: 1,
        unit_dollars: "0.00",
        line_type: "other",
        ...overrides,
    };
}

export default function FirstInvoiceDraftModal({ open, onOpenChange, rental, invoice, onSaved }) {
    const isEdit = !!invoice && invoice.status === "draft";
    const [issueDate, setIssueDate] = useState(todayIso());
    const [periodStart, setPeriodStart] = useState(todayIso());
    const [periodEnd, setPeriodEnd] = useState("");
    const [rows, setRows] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmIssue, setConfirmIssue] = useState(false);
    const [customerName, setCustomerName] = useState("");

    // Phase FIRST-INV rev — warning when the rental has no deposit
    // amount set. Surfaced on both fresh drafts (from the auto-populate
    // path) and edits (derived live from the current rental state).
    const showNoDepositWarning = useMemo(() => {
        const dep = rental?.deposit;
        const cents = Number(dep?.amount_cents || 0);
        return !dep || cents <= 0;
    }, [rental]);

    // Seed form on open — either from an existing draft invoice (edit
    // mode) or by auto-populating from the rental (create mode).
    useEffect(() => {
        if (!open) return;
        (async () => {
            let terms = "Payment due upon receipt via ACH or wire";
            try {
                const s = await api.get("/admin/system-settings");
                if (s.data?.default_payment_terms) terms = s.data.default_payment_terms;
            } catch (_) { /* ignore */ }
            // Best-effort customer name.
            let cname = rental?.customer_name || "";
            if (!cname && rental?.customer_id) {
                try {
                    const c = await api.get(`/admin/customers/${rental.customer_id}`);
                    cname = c.data?.company_name || "";
                } catch (_) { /* ignore */ }
            }
            setCustomerName(cname);

            if (isEdit && invoice) {
                setIssueDate((invoice.issue_date || todayIso()).slice(0, 10));
                setPeriodStart((invoice.period_start || rental?.start_date || todayIso()).slice(0, 10));
                setPeriodEnd(invoice.period_end ? invoice.period_end.slice(0, 10) : "");
                setPaymentTerms(invoice.payment_terms_text || terms);
                setNotes(invoice.notes || "");
                setRows((invoice.line_items || []).map((li) => newRow({
                    description: li.description || "",
                    quantity: Number(li.quantity) || 1,
                    unit_dollars: dollarsFromCents(li.unit_amount_cents),
                    line_type: li.line_type || "other",
                })));
            } else {
                const start = (rental?.start_date || todayIso()).slice(0, 10);
                setIssueDate(todayIso());
                setPeriodStart(start);
                setPeriodEnd("");  // Phase FIRST-INV rev — open-ended default.
                setPaymentTerms(terms);
                setNotes("");
                // Phase FIRST-INV rev — description parity with recurring
                // generator ("Monthly rent — {label} #{unit}"). Deposit
                // line is ALWAYS included even at $0.
                const eqKey = rental?.equipment_type || "rental";
                const eqLabel = eqKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                const unit = rental?.unit_number || "—";
                const monthly = Number(rental?.monthly_rate || 0);
                const seeded = [
                    newRow({
                        description: `Monthly rent — ${eqLabel} #${unit}`,
                        quantity: 1,
                        unit_dollars: monthly.toFixed(2),
                        line_type: "rent",
                    }),
                ];
                const dep = rental?.deposit;
                const depCents = Number(dep?.amount_cents || 0);
                seeded.push(newRow({
                    description: "Security deposit",
                    quantity: 1,
                    unit_dollars: dollarsFromCents(depCents),
                    line_type: "security_deposit",
                }));
                setRows(seeded);
            }
        })();
    }, [open, rental, invoice, isEdit]);

    // Phase FIRST-INV rev — due_date field removed from UI; payment
    // terms text is the customer-visible signal.

    const subtotal = useMemo(() => rows.reduce((acc, r) => {
        const q = Number(r.quantity) || 0;
        const cents = centsFromDollars(r.unit_dollars);
        return acc + Math.round(q * cents);
    }, 0), [rows]);
    const total = subtotal; // No tax.

    const updateRow = (key, field, value) => {
        setRows((prev) => prev.map((r) => (r._key === key ? { ...r, [field]: value } : r)));
    };
    const removeRow = (key) => setRows((prev) => prev.filter((r) => r._key !== key));
    const addRow = () => setRows((prev) => [...prev, newRow()]);

    const validate = () => {
        if (!rows.length) return "Add at least one line item.";
        for (const r of rows) {
            if (!(r.description || "").trim()) return "Every line needs a description.";
            const q = Number(r.quantity);
            if (!isFinite(q) || q <= 0) return "Every line needs a quantity > 0.";
            const c = centsFromDollars(r.unit_dollars);
            if (!(c >= 0)) return "Every line needs a valid unit price.";
        }
        if (!issueDate) return "Issue date is required.";
        return null;
    };

    const collectPayload = () => ({
        line_items: rows.map((r) => ({
            description: r.description.trim(),
            quantity: Number(r.quantity),
            unit_amount_cents: centsFromDollars(r.unit_dollars),
            line_type: r.line_type || "other",
        })),
        issue_date: issueDate,
        period_start: periodStart || null,
        period_end: periodEnd || null,
        payment_terms_text: (paymentTerms || "").trim(),
        notes: (notes || "").trim() || null,
    });

    const saveDraft = async () => {
        const err = validate();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            let saved;
            const payload = collectPayload();
            if (isEdit) {
                const patch = {
                    line_items: payload.line_items,
                    issue_date: payload.issue_date,
                    notes: payload.notes,
                };
                const { data } = await api.patch(`/admin/invoices/${invoice.id}`, patch);
                saved = data;
            } else {
                const { data } = await api.post(
                    `/admin/rentals/${rental.id}/first-invoice/draft`,
                    payload,
                );
                saved = data;
            }
            toast.success(isEdit ? "Draft updated" : "Draft created");
            if (typeof onSaved === "function") onSaved(saved);
        } catch (err2) {
            toast.error(formatApiError(err2));
        } finally { setSubmitting(false); }
    };

    const issueNow = async () => {
        const err = validate();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            // Save first (as draft) — this ensures every edit lands even when
            // the admin never clicked "Save as draft" before hitting Issue.
            const payload = collectPayload();
            let target = invoice;
            if (isEdit) {
                await api.patch(`/admin/invoices/${invoice.id}`, {
                    line_items: payload.line_items,
                    issue_date: payload.issue_date,
                    notes: payload.notes,
                });
            } else {
                const { data } = await api.post(
                    `/admin/rentals/${rental.id}/first-invoice/draft`,
                    payload,
                );
                target = data;
            }
            await api.post(`/admin/invoices/${target.id}/issue`);
            toast.success("First invoice issued and emailed to customer");
            if (typeof onSaved === "function") onSaved(target);
        } catch (err2) {
            toast.error(formatApiError(err2));
        } finally { setSubmitting(false); setConfirmIssue(false); }
    };

    return (
        <>
            <Dialog open={open && !confirmIssue} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto"
                    data-testid="first-invoice-draft-modal">
                    <DialogHeader>
                        <DialogTitle>{isEdit ? "Edit first invoice" : "Create first invoice"}</DialogTitle>
                        <DialogDescription>
                            {isEdit
                                ? `Update the draft before issuing to the customer.`
                                : `Draft the first invoice for this rental. You can include the security deposit as a line item; on full payment it will auto-record on the rental's Security Deposit section.`}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {showNoDepositWarning ? (
                            <div className="text-sm rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2"
                                data-testid="fi-no-deposit-warning">
                                <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
                                <div className="text-amber-900">
                                    <div className="font-semibold">No security deposit amount set on the rental.</div>
                                    <div className="text-xs mt-0.5">
                                        This line item has been added at $0. Either enter a deposit amount here, or update
                                        the rental&apos;s deposit before issuing. If no deposit will be collected, delete
                                        this line.
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Period start</Label>
                                <Input type="date" value={periodStart}
                                    onChange={(e) => setPeriodStart(e.target.value)}
                                    data-testid="fi-period-start" />
                            </div>
                            <div>
                                <Label>Period end</Label>
                                <Input type="date" value={periodEnd}
                                    onChange={(e) => setPeriodEnd(e.target.value)}
                                    placeholder="Optional — leave blank for open-ended"
                                    data-testid="fi-period-end" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Issue date</Label>
                                <Input type="date" value={issueDate}
                                    onChange={(e) => setIssueDate(e.target.value)}
                                    data-testid="fi-issue-date" />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Line items</Label>
                                <Button type="button" variant="outline" size="sm"
                                    onClick={addRow} className="gap-1"
                                    data-testid="fi-add-line">
                                    <Plus size={14} /> Add line
                                </Button>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                                        <tr>
                                            <th className="p-2 text-left font-medium">Description</th>
                                            <th className="p-2 text-left font-medium w-24">Qty</th>
                                            <th className="p-2 text-left font-medium w-36">Unit price ($)</th>
                                            <th className="p-2 text-left font-medium w-40">Type</th>
                                            <th className="p-2 text-right font-medium w-28">Line total</th>
                                            <th className="p-2 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const lineTotal = Math.round((Number(r.quantity) || 0) * centsFromDollars(r.unit_dollars));
                                            return (
                                                <tr key={r._key} className="border-t border-slate-100">
                                                    <td className="p-2">
                                                        <Input value={r.description}
                                                            onChange={(e) => updateRow(r._key, "description", e.target.value)}
                                                            data-testid="fi-row-description"
                                                            placeholder="Description" />
                                                    </td>
                                                    <td className="p-2">
                                                        <Input type="number" min={0} step="0.01"
                                                            value={r.quantity}
                                                            onChange={(e) => updateRow(r._key, "quantity", e.target.value)}
                                                            data-testid="fi-row-qty" />
                                                    </td>
                                                    <td className="p-2">
                                                        <Input type="number" min={0} step="0.01"
                                                            value={r.unit_dollars}
                                                            onChange={(e) => updateRow(r._key, "unit_dollars", e.target.value)}
                                                            data-testid="fi-row-unit" />
                                                    </td>
                                                    <td className="p-2">
                                                        <Select value={r.line_type}
                                                            onValueChange={(v) => updateRow(r._key, "line_type", v)}>
                                                            <SelectTrigger data-testid="fi-row-type"><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                {LINE_TYPES.map((lt) => (
                                                                    <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </td>
                                                    <td className="p-2 text-right font-medium text-slate-800 tabular-nums">
                                                        {money(lineTotal)}
                                                    </td>
                                                    <td className="p-2 text-right">
                                                        <button type="button"
                                                            onClick={() => removeRow(r._key)}
                                                            className="text-slate-400 hover:text-red-600 p-1"
                                                            data-testid="fi-row-remove"
                                                            aria-label="Remove line">
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="bg-slate-50">
                                        <tr>
                                            <td colSpan={4} className="p-2 text-right text-xs text-slate-500 uppercase tracking-wide">
                                                Subtotal
                                            </td>
                                            <td className="p-2 text-right font-medium tabular-nums" data-testid="fi-subtotal">
                                                {money(subtotal)}
                                            </td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={4} className="p-2 text-right text-sm font-semibold text-slate-700">
                                                TOTAL
                                            </td>
                                            <td className="p-2 text-right text-base font-bold tabular-nums text-slate-900" data-testid="fi-total">
                                                {money(total)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div>
                            <Label>Payment terms</Label>
                            <Input value={paymentTerms}
                                onChange={(e) => setPaymentTerms(e.target.value)}
                                placeholder="e.g. Payment due upon receipt via ACH or wire"
                                data-testid="fi-payment-terms" />
                        </div>

                        <div>
                            <Label>Internal notes (admin only)</Label>
                            <Textarea rows={2} value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                data-testid="fi-notes" />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)}
                            disabled={submitting}
                            data-testid="fi-cancel-btn">Cancel</Button>
                        <Button variant="outline" onClick={saveDraft}
                            disabled={submitting}
                            data-testid="fi-save-draft-btn">Save as draft</Button>
                        <Button onClick={() => setConfirmIssue(true)}
                            disabled={submitting}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="fi-issue-btn">Issue invoice</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm-issue nested dialog */}
            <Dialog open={confirmIssue} onOpenChange={setConfirmIssue}>
                <DialogContent className="max-w-md" data-testid="fi-confirm-issue-modal">
                    <DialogHeader>
                        <DialogTitle>Issue first invoice?</DialogTitle>
                        <DialogDescription>
                            This will send an email with the invoice to <strong>{customerName || "the customer"}</strong>{" "}
                            and lock line-item edits. Continue?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-sm rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
                        <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
                        <div>
                            Total <strong>{money(total)}</strong> · Issue date <strong>{issueDate}</strong>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmIssue(false)}
                            disabled={submitting}
                            data-testid="fi-confirm-cancel">Back</Button>
                        <Button onClick={issueNow} disabled={submitting}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="fi-confirm-issue">
                            {submitting ? "Issuing…" : "Issue &amp; email"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
